import React, { useEffect, useState } from 'react';
import moment from 'moment';

import AccommodationSearchForm from '../AccommodationSearch/components/AccommodationSearchForm';
import CheckboxFilter from '../AccommodationSearch/components/CheckboxFilter';
import RadioButtonFilter from '../AccommodationSearch/components/RadioButtonFilter';
import RangeFilter from '../AccommodationSearch/components/RangeFilter';
import { useMediaQuery } from 'react-responsive';
import TravelSearchResults from './components/TravelSearchResults';
import Spinner from '../../components/Spinner';
import { sendTravelEvent, ecommerceViewItemList } from '../../components/GA4';

const productUrls = {
  Ruka: process.env.RUKA_TRAVELSEARCH_PRODUCTS_URL,
  Pyha: process.env.PYHA_TRAVELSEARCH_PRODUCTS_URL,
  Luosto: process.env.LUOSTO_TRAVELSEARCH_PRODUCTS_URL,
};
const PRODUCTS_URL = productUrls[drupalSettings.active_site];

const priceUrls = {
  Ruka: process.env.RUKA_TRAVELSEARCH_PRICE_URL,
  Pyha: process.env.PYHA_TRAVELSEARCH_PRICE_URL,
  Luosto: process.env.LUOSTO_TRAVELSEARCH_PRICE_URL,
};
const PRICE_URL = priceUrls[drupalSettings.active_site];

// Max setting for the price filter.
const MAX_PRICE = 3000;

const TravelSearch = () => {
  const API_DATE_FORMAT = 'YYYY-MM-DD';
  const urlParams = new URLSearchParams(window.location.search);

  const [startDate, setStartDate] = useState(
    urlParams.get('startDate')
      ? moment(urlParams.get('startDate'), API_DATE_FORMAT)
      : moment()
  );
  const [endDate, setEndDate] = useState(
    urlParams.get('endDate')
      ? moment(urlParams.get('endDate'), API_DATE_FORMAT)
      : moment().add(2, 'days')
  );

  const [adults, setAdults] = useState(parseInt(urlParams.get('adults')) || 2);
  const [children, setChildren] = useState(
    parseInt(urlParams.get('children')) || 0
  );
  const [accommodationProvidersDrupal, setAccommodationProvidersDrupal] =
    useState();
  const [activeUrlParams, setActiveUrlParams] = useState(urlParams.toString());

  const [liftDistanceFilter, setLiftDistanceFilter] = useState();
  const [skiTrailDistanceFilter, setSkiTrailDistanceFilter] = useState();
  const [villageDistanceFilter, setVillageDistanceFilter] = useState();
  const [activeFacilityFilters, setActiveFacilityFilters] = useState([]);
  const [activeSizeFilter, setActiveSizeFilter] = useState();
  const [priceFilter, setPriceFilter] = useState();

  const [sortType, setSortType] = useState('random');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isSortDisabled, setIsSortDisabled] = useState(true);

  const [searchResults, setSearchResults] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState();
  const [numberOfSearches, setNumberOfSearches] = useState(1);
  const [selectedPopularDate, setSelectedPopularDate] = useState({});
  const [showMap, setShowMap] = useState(false);
  const dataLayer = window.dataLayer;

  const facilitiesFilters = [
    { value: 'Fireplace', name: Drupal.t('Fire place') },
    { value: 'Sauna', name: Drupal.t('Sauna') },
    { value: 'HotTub', name: Drupal.t('Hot tub') },
    { value: 'WashingMachine', name: Drupal.t('Washing machine') },
    { value: 'Stove', name: Drupal.t('Stove') },
    { value: 'AllowPets', name: Drupal.t('Pets allowed') },
    { value: 'BreakfastIncluded', name: Drupal.t('Breakfast included') },
  ];
  const sizeFilters = [
    { name: Drupal.t('Studio'), value: 0 },
    { name: Drupal.t('1 bedroom'), value: 1 },
    { name: Drupal.t('2 bedrooms'), value: 2 },
    { name: Drupal.t('3 bedrooms'), value: 3 },
    { name: Drupal.t('4 bedrooms'), value: 4 },
  ];

  const [filters, setFilters] = useState(initFilters());

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 920px)' });
  const [filtersOpen, setFiltersOpen] = useState(
    isTabletOrMobile ? false : true
  );
  const searchResultsWrapperRef = React.useRef(null);

  const apiLanguages = { fi: 'fi-FI', en: 'en-US' };
  const apiLanguage = apiLanguages[drupalSettings.current_language];

  async function search(e) {
    if (e) {
      e.preventDefault();
      setErrorMessage();
      setSearchResults([]);
      setIsSortDisabled(true);
      searchResultsWrapperRef.current.scrollIntoView({ behavior: 'smooth' });

      if (showMap && filtersOpen) {
        setFiltersOpen(false);
      }

      await fetchApi();
    }
  }

  async function fetchApi() {
    if (!accommodationProvidersDrupal) {
      return;
    }

    setIsLoading(true);
    let errorMessage;

    try {
      errorMessage = 'Error fetching products';

      let start, duration;
      if (startDate && endDate) {
        start = startDate.format(API_DATE_FORMAT);
        duration = endDate.diff(startDate, 'days');
      } else {
        start = moment().format(API_DATE_FORMAT);
        duration = 2;
        setStartDate(moment());
        setEndDate(moment().add(2, 'days'));
      }

      const TravelsearchProductUrl = new URL(PRODUCTS_URL);

      TravelsearchProductUrl.searchParams.append('startDate', start);
      TravelsearchProductUrl.searchParams.append('duration', duration);
      TravelsearchProductUrl.searchParams.append('adults', adults);
      TravelsearchProductUrl.searchParams.append('children', children);
      TravelsearchProductUrl.searchParams.append('language', apiLanguage);

      const token = `${process.env.RUKA_ES_AUTH_TOKEN}`;

      const productsResponse = await fetch(TravelsearchProductUrl, {
        headers: {
          token: token,
        },
      });

      if (productsResponse.ok) {
        const data = await productsResponse.json();

        const accommodationProviderIds = accommodationProvidersDrupal.map(
          (provider) => provider.id
        );

        // Only show the accommodation providers saved in Drupal.
        const productResults = data.Result.filter((result) =>
          accommodationProviderIds.includes(result.CompanyID)
        ) || [];

        const productsWithPrice = productResults.filter((product) => {
          if (
            product &&
            product !== undefined &&
            product.Price &&
            product.Price.Price > 0
          ) {
            const accommodationProvider = accommodationProvidersDrupal.find(
              (provider) => provider.id === product.CompanyID
            );
            product.accommodationProvider = accommodationProvider;
            return product;
          }
        });

        // Set the default products (ones that are alwaysShow)
        setDefaults(() =>
          accommodationProvidersDrupal.filter((accommodationProvider) => {
            if (accommodationProvider.alwaysShow) {
              return accommodationProvider;
            }
          })
        );

        setSearchResults((searchResults) => [
          ...searchResults,
          ...productsWithPrice,
        ]);

        const productsWithoutPrice = productResults.filter((product) => {
          if (product && product !== undefined && !product.Price) {
            const accommodationProvider = accommodationProvidersDrupal.find(
              (provider) => provider.id === product.CompanyID
            );
            product.accommodationProvider = accommodationProvider;
            return product;
          }
        });

        if (productsWithoutPrice.length > 0) {
          errorMessage = 'Error fetching prices';
          await getPrice(productsWithoutPrice, start, duration);
        } else {
          setIsSortDisabled(false);
        }
      }
    } catch (e) {
      console.log('error fetching products', e);
      setIsSortDisabled(false);
      setErrorMessage(Drupal.t(errorMessage));
    }
    setIsLoading(false);
  }

  useEffect(() => {
    // Load the accommodation provider terms.
    async function getAccommodationProviders() {
      const accommoddationProvidersApiUrl = `${drupalSettings.base_url}/${drupalSettings.current_language}/jsonapi/taxonomy_term/accommodation_providers?include=field_image,field_image.field_media_image&sort=weight`;
      const accommodationProvidersResponse = await fetch(
        accommoddationProvidersApiUrl,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/vnd.api+json',
          },
        }
      );
      let accommodationsProvidersData = [];
      if (accommodationProvidersResponse.ok) {
        accommodationsProvidersData =
          await accommodationProvidersResponse.json();
      }

      const images = accommodationsProvidersData.included
        .filter((image) => image.type === 'file--file')
        .map((image) => image.attributes.uri.url);

      const accommodationProviders = accommodationsProvidersData.data.map(
        (data, i) => {
          let searchParams = null;
          if (data.attributes.field_search_parameters) {
            try {
              searchParams = JSON.parse(
                data.attributes.field_search_parameters
              );
            } catch (e) {
              console.log(e);
            }
          }
          const defaultBody = data.attributes.field_body_description
            ? data.attributes.field_body_description.value
            : '';
          return {
            id: data.attributes.field_id,
            name: data.attributes.name,
            additionalInfo: data.attributes.field_additional_info,
            alwaysShow: data.attributes.field_always_show,
            body: defaultBody,
            image: images[i],
            groupResults: data.attributes.field_group_results,
            searchParams,
            baseUrl: data.attributes.field_link.uri,
            external: data.attributes.field_external,
          };
        }
      );

      setAccommodationProvidersDrupal(accommodationProviders);
    }

    getAccommodationProviders();
  }, []);

  useEffect(() => {
    async function getProducts() {
      await fetchApi();
    }

    getProducts();
  }, [accommodationProvidersDrupal]);

  useEffect(() => {
    updateQueryParams();
  }, [fetchApi]);

  useEffect(() => {
    // To disable making the call when the component is initially rendered
    if (!isSortDisabled) {
      // Delay the function call because otherwise
      // every slider increment change would trigger a call
      const delayDebounceFn = setTimeout(() => {
        sendToGA4(filterResults(searchResults));
      }, 1000);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [
    liftDistanceFilter,
    skiTrailDistanceFilter,
    villageDistanceFilter,
    priceFilter,
    isSortDisabled,
    activeFacilityFilters,
    activeSizeFilter,
    sortOrder,
    sortType,
  ]);

  function sendToGA4(results) {
    let eventName;
    if (numberOfSearches === 1) {
      ecommerceViewItemList(
        dataLayer,
        false,
        filterResults(results).sort(sortResults(sortType, sortOrder)),
        accommodationProvidersDrupal
      );
      eventName = 'travel_primary_search';
    } else {
      eventName = 'travel_refine_search';
    }
    setNumberOfSearches(() => numberOfSearches + 1);
    sendTravelEvent(
      dataLayer,
      eventName,
      startDate,
      endDate,
      adults,
      children,
      `${sortType}.${sortOrder}`,
      activeSizeFilter,
      activeFacilityFilters,
      liftDistanceFilter === 10 ? null : liftDistanceFilter,
      priceFilter === MAX_PRICE ? null : priceFilter,
      skiTrailDistanceFilter === 10 ? null : skiTrailDistanceFilter,
      villageDistanceFilter === 10 ? null : villageDistanceFilter,
      filterResults(results).length,
      selectedPopularDate
    );
  }

  // Formats the url for external search sites.
  function getSearchUrl(accommodationProvider) {
    if (!accommodationProvider.searchParams || !startDate || !endDate) {
      return accommodationProvider.baseUrl;
    }

    const { baseUrl, searchParams } = accommodationProvider;
    const lang = searchParams.languageMapping
      ? searchParams.languageMapping[drupalSettings.current_language]
      : '';

    let searchUrl = baseUrl;

    // Some sites have the language as a paremeter, some don't.
    if (!searchParams.languageParam) {
      searchUrl += lang;
    }

    searchUrl += `${searchParams.defaultQuery}&${
      searchParams.startDate
    }=${startDate.format(searchParams.dateFormat)}&${
      searchParams.endDate
    }=${endDate.format(searchParams.dateFormat)}`;

    if (searchParams.languageParam) {
      searchUrl += `&${searchParams.languageParam}=${lang}`;
    }

    // With Scandic, the children need to each be specified separately, with age and bed type.
    // Since we don't ask the ages of the children here, we just set each to 12.
    if (searchParams.provider && searchParams.provider === 'scandic') {
      searchUrl += `&room[0].adults=${adults}`;
      for (let i = 0; i < children; i++) {
        searchUrl += `&room[0].child[${i}].age=12&room[0].child[${i}].bed=2`;
      }
      return searchUrl;
    }

    // Some search sites have adults and children separately,
    // some just have total number of people.
    if (searchParams.totalPersons) {
      searchUrl += `&${searchParams.totalPersons}=${adults + children}`;
    } else if (searchParams.adults && searchParams.children) {
      searchUrl += `&${searchParams.adults}=${adults}&${searchParams.children}=${children}`;
    }

    return searchUrl;
  }

  // Some accommodation providers contain placeholders in the external urls.
  // Handling the replacements here.
  function getProductUrl(product) {
    if (product.Price.Url && product.Price.Url.length > 0) {
      return product.Price.Url;
    }

    // In case the url in price is empty.

    if (!startDate || !endDate) {
      return;
    }
    let productUrl = getAttribute(product, 'ExternalUrl');
    if (!productUrl) {
      return product.accommodationProvider.baseUrl;
    }

    return productUrl
      .replace('{DbStartDate}', startDate.format(API_DATE_FORMAT))
      .replace('{DbEndDate}', endDate.format(API_DATE_FORMAT))
      .replace('{NetHotelPersons}', adults)
      .replace('{NetHotelChildren}', children);
  }

  function getAttribute(product, attribute, valueType = 'Value') {
    const value = product.Fields.find((field) => field.Name === attribute);
    if (value && value[valueType]) {
      return value[valueType];
    } else {
      return null;
    }
  }

  async function getPrice(productsWithoutPrice, startDateString, duration) {
    const TravelsearchPriceUrl = new URL(PRICE_URL);

    TravelsearchPriceUrl.searchParams.append('startDate', startDateString);
    TravelsearchPriceUrl.searchParams.append('duration', duration);
    TravelsearchPriceUrl.searchParams.append('adults', adults);
    TravelsearchPriceUrl.searchParams.append('children', children);
    TravelsearchPriceUrl.searchParams.append('language', apiLanguage);

    const token = `${process.env.RUKA_ES_AUTH_TOKEN}`;

    let i = 0;

    try {
      productsWithoutPrice.forEach(async (product) => {
        TravelsearchPriceUrl.searchParams.delete('ProductID');
        TravelsearchPriceUrl.searchParams.append(
          'ProductID',
          product.ProductID
        );

        const response = await fetch(TravelsearchPriceUrl, {
          headers: {
            token: token,
          },
        });

        if (response.ok) {
          i++;
          const data = await response.json();
          if (data.Price) {
            product.Price = data.Price;
            setSearchResults((searchResults) => [...searchResults, product]);
          }

          if (i === productsWithoutPrice.length) {
            setIsSortDisabled(false);
          }
        }
      });
    } catch (e) {
      console.error('error', e);
    }
  }

  function formatPrice(price) {
    return Number.parseFloat(price).toFixed(0);
  }

  function handleShowFilters() {
    if (filtersOpen) {
      setFiltersOpen(false);
    } else {
      setFiltersOpen(true);
    }
  }

  function updateQueryParams() {
    if (startDate && endDate) {
      urlParams.set('startDate', startDate.format(API_DATE_FORMAT));
      urlParams.set('endDate', endDate.format(API_DATE_FORMAT));
      urlParams.set('adults', adults);
      urlParams.set('children', children);
    } else {
      urlParams.delete('startDate');
      urlParams.delete('endDate');
      urlParams.delete('adults');
      urlParams.delete('children');
    }
    setActiveUrlParams(urlParams.toString());
    window.history.replaceState(
      {},
      '',
      `${location.pathname}?${activeUrlParams}`
    );
  }

  function filterSkiTrailDistance(result) {
    if (result.products) {
      return result.products.filter(filterSkiTrailDistance);
    }
    if (!skiTrailDistanceFilter || parseFloat(skiTrailDistanceFilter) === 10) {
      return true;
    }
    return (
      parseFloat(getAttribute(result, 'DistanceCrossCountrySkiingTrack')) <=
      parseFloat(skiTrailDistanceFilter)
    );
  }
  function filterVillageDistance(result) {
    if (result.products) {
      return result.products.filter(filterVillageDistance);
    }
    if (!villageDistanceFilter || parseFloat(villageDistanceFilter) === 10) {
      return true;
    }
    return (
      parseFloat(getAttribute(result, 'DistanceRuka')) <=
      parseFloat(villageDistanceFilter)
    );
  }
  function filterLiftDistance(result) {
    if (result.products) {
      return result.products.filter(filterLiftDistance);
    }
    if (!liftDistanceFilter || parseFloat(liftDistanceFilter) === 10) {
      return true;
    }
    return (
      parseFloat(getAttribute(result, 'DistanceSkiLift')) <=
      parseFloat(liftDistanceFilter)
    );
  }

  function filterFacilities(result) {
    if (result.products) {
      return result.products.filter(filterFacilities);
    }
    if (!activeFacilityFilters.length) {
      return true;
    } else {
      return activeFacilityFilters.every((value) =>
        getAttribute(result, value, 'BooleanData')
      );
    }
  }

  function getFacilityFilter(filter) {
    return activeFacilityFilters.some(
      (activeFilter) => activeFilter === filter
    );
  }

  function filterSizes(result) {
    if (result.products) {
      return result.products.filter(filterSizes);
    }
    if (!activeSizeFilter || activeSizeFilter.value === 0) {
      return true;
    }
    return getAttribute(result, 'Bedrooms', 'IntegerData') >= activeSizeFilter;
  }

  /**
   * Filter pets for Ski-Inn apartments to get rid of duplicate results.
   * Unlike for other companies, this is done even if the pets filtering
   * isn't checked, filtering out either pet or non-pet rooms.
   */
  function filterSkiInnPets(product) {
    // Collection of products, won't be filtered here.
    if (product.CompanyName !== 'Ski-Inn') {
      return true;
    }

    const apartmentAllowsPets = getAttribute(
      product,
      'AllowPets',
      'BooleanData'
    );
    const allowPetsFilter = getFacilityFilter('AllowPets');

    return !!apartmentAllowsPets === allowPetsFilter;
  }

  /**
   * Filter out products by maximum allowed price.
   */
  function filterPrice(product) {
    // No price available for the product.
    if (!product?.Price?.Price) {
      return true;
    }

    // If filter is set to max, return all products.
    if (MAX_PRICE === priceFilter) {
      return true;
    }

    return product.Price.Price <= priceFilter;
  }

  function filterResults(results) {
    return results
      .filter(filterLiftDistance)
      .filter(filterVillageDistance)
      .filter(filterSkiTrailDistance)
      .filter(filterFacilities)
      .filter(filterSizes)
      .filter(filterSkiInnPets)
      .filter(filterPrice);
  }

  function sortResults(type, order) {
    // Random is actually just the initial order.
    if (type === 'random') {
      return () => 0;
    } else if (type === 'field_title') {
      return (a, b) => (a.Name > b.Name ? 1 : -1);
    } else if (type === 'price') {
      if (order === 'desc') {
        return (a, b) =>
          (a.maxPrice ?? a.Price.Price) < (b.maxPrice ?? b.Price.Price)
            ? 1
            : -1;
      } else {
        return (a, b) =>
          (a.minPrice ?? a.Price.Price) > (b.minPrice ?? b.Price.Price)
            ? 1
            : -1;
      }
    } else if (type === 'field_area_m2') {
      if (order === 'desc') {
        return (a, b) =>
          (a.maxSize ??
            parseFloat(a.Fields.find((field) => field.Name === 'Area').Value)) <
          (b.maxSize ??
            parseFloat(b.Fields.find((field) => field.Name === 'Area').Value))
            ? 1
            : -1;
      } else {
        return (a, b) =>
          (a.minSize ?? parseFloat(getAttribute(a, 'Area', 'Value'))) >
          (b.minSize ?? parseFloat(getAttribute(b, 'Area', 'Value')))
            ? 1
            : -1;
      }
    }
  }

  /**
   * Toggle showing the map.
   */
  function toggleMap() {
    if (!isTabletOrMobile && showMap) {
      setFiltersOpen(true);
    } else {
      setFiltersOpen(false);
    }

    setShowMap(!showMap);
  }

  function initFilters() {
    return (
      <div className="accommodation-search__filter-inner-wrapper">
        <h3>{Drupal.t('Search conditions')}</h3>
        <h4
          onClick={clearFilters}
          className="accommodation-search__clear-filters"
        >
          {Drupal.t('Clear')}
        </h4>

        <h4>{Drupal.t('Price')}</h4>
        <div>
          <RangeFilter
            key={new Date().getTime() + 'price'}
            name="price"
            label={Drupal.t('Maximum price')}
            min={0}
            max={MAX_PRICE}
            step={100}
            setState={setPriceFilter}
            value={priceFilter}
            unit="€"
            showMaxOrOver={true}
            disabled={isLoading}
          />
        </div>

        <h4>{Drupal.t('Distances')}</h4>
        <div className="accommodation-search__distance-filter-wrapper">
          <RangeFilter
            key={new Date().getTime() + 'lift'}
            name="lift"
            label={Drupal.t('To lift')}
            min={0}
            max={10}
            step={0.5}
            setState={setLiftDistanceFilter}
            unit="km"
            showMaxOrOver={true}
          />
          <RangeFilter
            key={new Date().getTime() + 'skitrail'}
            name="skitrail"
            label={Drupal.t('To ski trail')}
            min={0}
            max={10}
            step={0.5}
            setState={setSkiTrailDistanceFilter}
            unit="km"
            showMaxOrOver={true}
          />
          <RangeFilter
            key={new Date().getTime() + 'village'}
            name="village"
            label={
              drupalSettings.active_site == 'Ruka'
                ? Drupal.t('To Ruka village')
                : Drupal.t('To Pyhä village')
            }
            min={0}
            max={10}
            step={0.5}
            setState={setVillageDistanceFilter}
            unit="km"
            showMaxOrOver={true}
          />
        </div>

        <h4>{Drupal.t('Facilities')}</h4>
        {facilitiesFilters.map((filter, i) => (
          <CheckboxFilter
            key={new Date().getTime() + i}
            setState={setActiveFacilityFilters}
            name={filter.name}
            value={filter.value}
          />
        ))}
        <h4>{Drupal.t('Rooms')}</h4>
        <div className="form-item--radios">
          {sizeFilters.map((filter, i) => (
            <RadioButtonFilter
              key={new Date().getTime() + i}
              setState={setActiveSizeFilter}
              name={'rooms'}
              value={`${filter.value}`}
              title={filter.name}
              default={`${sizeFilters[0].value}`}
            />
          ))}
        </div>
      </div>
    );
  }

  const toggleFiltersButton = (
    <button
      onClick={handleShowFilters}
      className="button button--gray travel-search--filters"
    >
      {!filtersOpen
        ? Drupal.t('Show filtering options')
        : Drupal.t('Hide filtering options')}
    </button>
  );

  function clearFilters() {
    setLiftDistanceFilter(10);
    setSkiTrailDistanceFilter(10);
    setVillageDistanceFilter(10);
    setActiveFacilityFilters([]);
    setActiveSizeFilter();
    setPriceFilter(MAX_PRICE);

    setFilters(initFilters());
  }

  return (
    <div className="accommodation-search travel-search">
      <AccommodationSearchForm
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        adults={adults}
        setAdults={setAdults}
        kids={children}
        setChildren={setChildren}
        maxGuests={14}
        onSubmit={search}
        buttonText={Drupal.t(
          'Search',
          {},
          { context: 'Travel search external' }
        )}
        availabilityFound={true}
        roomAllowsPets={false}
        allowInfants={false}
        selectDatesLabel={Drupal.t('Dates')}
        popularDatesLabel={Drupal.t('Or choose from quick links')}
        adultsLabel={Drupal.t('Adults')}
        kidsLabel={Drupal.t('Kids 0-12 yrs')}
        startDatePlaceholderText={Drupal.t('Arrival')}
        endDatePlaceholderText={Drupal.t('Departure')}
        errorMessage={errorMessage}
        setSelectedPopularDate={setSelectedPopularDate}
      />

      <div
        className={`accommodation-search__content-wrapper ${
          showMap ? 'show-map' : ''
        }`}
        ref={searchResultsWrapperRef}
      >
        {isLoading && <Spinner />}

        {!isLoading && showMap && (
          <div className="accommodation-search__map-controls">
            {toggleFiltersButton}

            <button
              onClick={toggleMap}
              className="button button--gray button--close-map"
            >
              {Drupal.t('Show as list')}
            </button>
          </div>
        )}

        {!isLoading && (
          <TravelSearchResults
            products={searchResults}
            defaults={defaults}
            filterResults={filterResults}
            getSearchUrl={getSearchUrl}
            sortResults={sortResults}
            sortType={sortType}
            sortOrder={sortOrder}
            getAttribute={getAttribute}
            formatPrice={formatPrice}
            setSortType={setSortType}
            setSortOrder={setSortOrder}
            accommodationProviders={accommodationProvidersDrupal}
            getProductUrl={getProductUrl}
            isSortDisabled={isSortDisabled}
            showMap={showMap}
            toggleMap={toggleMap}
          />
        )}

        {!showMap && isTabletOrMobile && toggleFiltersButton}

        <div
          className={`accommodation-search__filter-wrapper ${
            !filtersOpen ? 'visually-hidden' : ''
          }`}
        >
          {filters}
        </div>
      </div>
    </div>
  );
};

export default TravelSearch;
